import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { usePageTitle } from 'utils/pageTitle';
import { get, post, del } from 'data/services/apiService';
import { getApiUrl } from 'data/services/apiConstants';
import { showToast } from 'data/reducers/toastReducer';
import AdminLayout from 'pages/_rd_dashboard/AdminLayout';
import StandardModal from 'modals/StandardModal';

import Spinner from 'components/Spinner';
import Paginator from 'components/Paginator';

import './AdminStreetViewReports.css';

const PER_PAGE = 20;

const AdminStreetViewReports = () => {
	usePageTitle('Admin | Street View Reports');
	const dispatch = useDispatch();
	const authToken = useSelector((state) => state.user.authToken);

	const [page, setPage] = useState(1);
	const [result, setResult] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [hasError, setHasError] = useState(false);
	// property_id of the row whose candidates are being fetched, so only that row's button disables/spins
	const [regeneratingId, setRegeneratingId] = useState(null);
	// report.id of the row currently being dismissed
	const [deletingId, setDeletingId] = useState(null);
	// { report, candidates: [{ pano_id, heading, pitch, objectUrl }] } once candidates load, else null
	const [candidatePicker, setCandidatePicker] = useState(null);
	// true while the admin's chosen candidate is being saved
	const [isSelecting, setIsSelecting] = useState(false);

	useEffect(() => {
		let isCurrent = true;
		setIsLoading(true);
		setHasError(false);
		get({
			url: `${getApiUrl()}/api/admin/street-view-reports?page=${page}&per_page=${PER_PAGE}`,
			authToken,
			dispatch,
		})
			.then((response) => {
				if (isCurrent) setResult(response);
			})
			.catch(() => {
				if (isCurrent) {
					setResult(null);
					setHasError(true);
				}
			})
			.finally(() => {
				if (isCurrent) setIsLoading(false);
			});
		// ignore a response for a page the admin has already left
		return () => {
			isCurrent = false;
		};
	}, [page, authToken, dispatch]);

	// Candidate images are served behind admin auth, so a plain <img src> can't
	// carry the bearer token -- fetch each as a blob and point the <img> at an
	// object URL instead, same approach apiService's download() uses.
	const fetchCandidateImage = (imageUrl) =>
		fetch(new Request(`${getApiUrl()}${imageUrl}`, {
			headers: { authorization: `Bearer ${authToken}` },
		}))
			.then((response) => response.blob())
			.then((blob) => window.URL.createObjectURL(blob));

	const revokeCandidateImages = (candidates) => {
		(candidates || []).forEach((candidate) => {
			if (candidate.objectUrl) window.URL.revokeObjectURL(candidate.objectUrl);
		});
	};

	const handleRegenerate = (report) => {
		setRegeneratingId(report.property_id);
		get({
			url: `${getApiUrl()}/api/admin/street-view-reports/${report.property_id}/candidates`,
			authToken,
			dispatch,
		})
			.then((response) => {
				const candidates = response?.data?.candidates || [];
				return Promise.all(
					candidates.map((candidate) =>
						fetchCandidateImage(candidate.image_url).then((objectUrl) => ({
							...candidate,
							objectUrl,
						})),
					),
				);
			})
			.then((candidates) => {
				setCandidatePicker({ report, candidates });
			})
			.catch((error) => {
				dispatch(
					showToast({
						title: 'Street View',
						body: error?.message || 'Could not load candidate images.',
						type: 'danger',
					}),
				);
			})
			.finally(() => {
				setRegeneratingId(null);
			});
	};

	const handleCancelPicker = () => {
		revokeCandidateImages(candidatePicker?.candidates);
		setCandidatePicker(null);
	};

	const handleSelectCandidate = (candidate) => {
		const report = candidatePicker.report;
		setIsSelecting(true);
		post({
			url: `${getApiUrl()}/api/admin/street-view-reports/${report.property_id}/select`,
			data: {
				pano_id: candidate.pano_id,
				heading: candidate.heading,
				pitch: candidate.pitch,
			},
			authToken,
			dispatch,
		})
			.then(() => {
				setResult((prev) =>
					prev
						? {
								...prev,
								data: prev.data.map((r) =>
									r.id === report.id
										? { ...r, resolved_at: new Date().toISOString() }
										: r,
								),
							}
						: prev,
				);
				dispatch(
					showToast({
						title: 'Street View',
						body: 'Image updated.',
						type: 'success',
					}),
				);
				revokeCandidateImages(candidatePicker?.candidates);
				setCandidatePicker(null);
			})
			.catch((error) => {
				dispatch(
					showToast({
						title: 'Street View',
						body: error?.message || 'Could not save the chosen image.',
						type: 'danger',
					}),
				);
			})
			.finally(() => {
				setIsSelecting(false);
			});
	};

	const handleDelete = (reportId) => {
		setDeletingId(reportId);
		del({
			url: `${getApiUrl()}/api/admin/street-view-reports/${reportId}`,
			authToken,
			dispatch,
		})
			.then(() => {
				setResult((prev) =>
					prev
						? {
								...prev,
								data: prev.data.filter((r) => r.id !== reportId),
								total: (prev.total || 1) - 1,
							}
						: prev,
				);
			})
			.catch((error) => {
				dispatch(
					showToast({
						title: 'Street View',
						body: error?.message || 'Could not dismiss the report.',
						type: 'danger',
					}),
				);
			})
			.finally(() => {
				setDeletingId(null);
			});
	};

	const rows = result?.data || [];

	return (
		<AdminLayout>
			<div className="dashboard-portfolio admin-street-view-reports">
				<h1>Street View Reports</h1>
				<p className="admin-svr-hint">
					Properties users have flagged as showing a wrong Street View
					thumbnail.
					{result?.total !== undefined &&
						` ${result.total.toLocaleString()} total.`}
				</p>

				{isLoading ? (
					<Spinner />
				) : (
					<div className="pb-4">
						{hasError && (
							<div className="alert alert-danger">
								Could not load reports. Please try again.
							</div>
						)}
						<div className="dashboard-table-wrapper">
							<table className="table table-striped table-rounded">
								<thead>
									<tr>
										<th>Property</th>
										<th>Reported By</th>
										<th>Reported At</th>
										<th aria-label="Actions" />
									</tr>
								</thead>
								<tbody>
									{!hasError && rows.length === 0 && (
										<tr>
											<td colSpan={4} className="text-center text-muted">
												No reports.
											</td>
										</tr>
									)}
									{rows.map((report) => {
										const isRegenerated = !!report.resolved_at;
										const isRegenerating =
											regeneratingId === report.property_id;
										const isDeleting = deletingId === report.id;

										return (
											<tr key={report.id}>
												<td className="dashboard-table-property-address-cell">
													{report.property?.address || '-'}
												</td>
												<td>
													<div>{report.user?.name || '-'}</div>
													<div className="admin-svr-muted">
														{report.user?.email || ''}
													</div>
												</td>
												<td className="dashboard-date-time-cell">
													{report.created_at || '-'}
												</td>
												<td className="admin-svr-actions-cell">
													{isRegenerated && (
														<span className="badge bg-success admin-svr-done-badge">
															Done
														</span>
													)}
													{!isRegenerated && (
														<button
															type="button"
															className="btn btn-sm btn-outline-dark"
															disabled={isRegenerating}
															onClick={() => handleRegenerate(report)}
														>
															{isRegenerating
																? 'Regenerating…'
																: 'Regenerate'}
														</button>
													)}
													<button
														type="button"
														className="btn btn-sm btn-outline-danger"
														disabled={isDeleting}
														onClick={() => handleDelete(report.id)}
													>
														{isDeleting ? 'Deleting…' : 'Delete'}
													</button>
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
						<Paginator
							currentPage={result?.current_page || 1}
							totalPages={result?.last_page || 1}
							setPage={setPage}
						/>
					</div>
				)}

				{candidatePicker && (
					<StandardModal
						title="Choose a Street View image"
						subtitle={candidatePicker.report.property?.address}
						closeOnOutsideClick
						size="lg"
						noFooterClose
						footerButtons={
							<button
								type="button"
								className="btn btn-secondary"
								disabled={isSelecting}
								onClick={handleCancelPicker}
							>
								Cancel
							</button>
						}
						onClose={handleCancelPicker}
					>
						{candidatePicker.candidates.length === 0 ? (
							<p className="text-muted mb-0">
								No candidate images were found for this property.
							</p>
						) : (
							<div className="admin-svr-candidate-grid">
								{candidatePicker.candidates.map((candidate) => (
									<div
										className="admin-svr-candidate"
										key={candidate.pano_id}
									>
										<img
											src={candidate.objectUrl}
											alt="Street View candidate"
										/>
										<button
											type="button"
											className="btn btn-sm btn-primary w-100 mt-2"
											disabled={isSelecting}
											onClick={() => handleSelectCandidate(candidate)}
										>
											{isSelecting ? 'Saving…' : 'Use this image'}
										</button>
									</div>
								))}
							</div>
						)}
					</StandardModal>
				)}
			</div>
		</AdminLayout>
	);
};

export default AdminStreetViewReports;
